import {
  addDays,
  addMonths,
  addYears,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  endOfDay,
  getMonth,
  getYear,
  isAfter,
  isBefore,
  isEqual,
  isLastDayOfMonth,
  isValid,
  lastDayOfMonth,
  max,
  min,
  startOfToday,
  startOfTomorrow,
} from "date-fns";
import { FormatDateOptions } from "react-intl";
import { LocalizationHelperInstance } from "utils/helpers";
import { DateConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DateRange } from "models/formTypes/util";

const DateHelper = {
  today: () => startOfToday(),
  tomorrow: () => startOfTomorrow(),
  parseDateOnly: (isoString: string): Date => {
    const [y, m, d] = isoString.substring(0, 10).split("-").map(Number);
    return new Date(y, m - 1, d);
  },
  /** 23:59:59.999 local time: the end a date-range picker stores, so a day-long range has start < end. */
  getEndOfDay: (date: Date): Date => endOfDay(date),
  getYearMonth: (date: Date): number => {
    date = new Date(date);
    return getYear(date) * 100 + (getMonth(date) + 1);
  },

  formatYearMonth: (date: { month: number; year: number }): number => {
    return date.year * 100 + date.month;
  },

  splitYearMonth: (yearMonth: number): { month: number; year: number } => {
    return { month: yearMonth % 100, year: Math.trunc(yearMonth / 100) };
  },

  getYearMonthByDate: (date: Date): { month: number; year: number } => {
    return DateHelper.splitYearMonth(DateHelper.getYearMonth(date));
  },

  getMonth: (date: Date): number => {
    return getMonth(date) + 1;
  },

  getYear: (date: Date): number => {
    return getYear(new Date(date));
  },

  getDateFromYearMonth: (yearMonth: number): Date => {
    return new Date(yearMonth / 100, (yearMonth % 100) - 1, 1);
  },

  getYearMonthRange: (startDate: Date, endDate: Date): number[] => {
    const result: number[] = [];
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    while (current <= end) {
      result.push(DateHelper.getYearMonth(current));
      current.setMonth(current.getMonth() + 1);
    }
    return result;
  },

  getLastDayOfMonth: (date: Date) => {
    return lastDayOfMonth(date);
  },

  getFirstDayOfMonth: (date: Date) => {
    return new Date(date.setDate(1));
  },

  getDayOfMonth: (date: Date, day: number) => {
    return new Date(date.setDate(day));
  },

  getFirstDayOfPreviousMonth: (date: Date) => {
    return new Date(addMonths(date, -1).setDate(1));
  },

  getFirstDayOfNextMonth: (date: Date) => {
    return new Date(addMonths(date, 1).setDate(1));
  },

  getLastDayOfNextMonth: (date: Date) => {
    return lastDayOfMonth(addMonths(date, 1));
  },

  getLastDayOfPreviousMonth: (date: Date) => {
    return lastDayOfMonth(addMonths(date, -1));
  },

  getFirstDayOfYear: (year: number) => {
    return new Date(year, 0, 1);
  },

  getLastDayOfYear: (year: number) => {
    return new Date(year, 11, 31);
  },

  formatMonthRange: (startDate?: Date, endDate?: Date, showSingleDate?: boolean) => {
    if (startDate && endDate) {
      startDate = new Date(startDate);
      endDate = new Date(endDate);
      if (getYear(startDate) === getYear(endDate))
        if (getMonth(startDate) === getMonth(endDate))
          return `${LocalizationHelperInstance.getFormattedDate(startDate, DateConstants.MonthYearShortConfig)}`;
        else
          return `${LocalizationHelperInstance.getFormattedDate(
            startDate,
            DateConstants.MonthShortConfig
          )} - ${LocalizationHelperInstance.getFormattedDate(endDate, DateConstants.MonthYearShortConfig)}`;
      else
        return `${LocalizationHelperInstance.getFormattedDate(
          startDate,
          DateConstants.MonthYearShortConfig
        )} - ${LocalizationHelperInstance.getFormattedDate(endDate, DateConstants.MonthYearShortConfig)}`;
    } else if (showSingleDate) {
      if (startDate)
        return `${LocalizationHelperInstance.getFormattedDate(startDate, DateConstants.MonthYearShortConfig)}`;
      else if (endDate)
        return `- ${LocalizationHelperInstance.getFormattedDate(endDate, DateConstants.MonthYearShortConfig)}`;
    }
    return "";
  },

  formatAPIYearMonthRange: (
    startMonth?: WintSuperkollAPIModel.YearAndMonth,
    endMonth?: WintSuperkollAPIModel.YearAndMonth
  ) => {
    return `${startMonth ? DateHelper.formatMonth(DateHelper.getDateFromAPIYearMonth(startMonth)) : ""} - ${
      endMonth ? DateHelper.formatMonth(DateHelper.getDateFromAPIYearMonth(endMonth)) : ""
    }`;
  },

  formatDateRange: (startDate?: Date, endDate?: Date, separator?: string, config?: FormatDateOptions) => {
    if (startDate || endDate) {
      return `${startDate ? LocalizationHelperInstance.getFormattedDate(startDate, config) : ""}${
        endDate
          ? ` ${
              separator
                ? separator
                : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.To).toLowerCase()
            } ${LocalizationHelperInstance.getFormattedDate(endDate, config)}`
          : ""
      }`;
    } else return " ";
  },
  getYearRange: (startDate: Date, endDate: Date) => {
    const startYear = getYear(new Date(startDate));
    const endYear = getYear(new Date(endDate));

    if (startYear === endYear) return `${startYear}`;
    return `${startYear} - ${endYear}`;
  },

  formatMonth: (date?: Date) => {
    return date ? LocalizationHelperInstance.getFormattedDate(date, DateConstants.MonthYearShortConfig) : "";
  },

  formatAPIYearMonthToNumericString: (yearMonth: WintSuperkollAPIModel.YearAndMonth, addMonths?: number) => {
    const date = DateHelper.getDateFromYearMonth(DateHelper.formatYearMonth(yearMonth));
    const dateToFormat = addMonths ? DateHelper.addMonths(date, addMonths) : date;
    return LocalizationHelperInstance.getFormattedDate(dateToFormat, DateConstants.MonthYearNumericConfig);
  },

  formatDateToNumericString: (date: Date, addMonths?: number) => {
    const dateToFormat = addMonths ? DateHelper.addMonths(date, addMonths) : date;
    return LocalizationHelperInstance.getFormattedDate(dateToFormat, DateConstants.MonthYearNumericConfig);
  },

  getDateFromAPIYearMonth: (yearMonth: WintSuperkollAPIModel.YearAndMonth) => {
    return DateHelper.getDateFromYearMonth(DateHelper.formatYearMonth(yearMonth));
  },

  formatDateDistance: (startDate: Date, endDate: Date, endPhrase?: string) => {
    startDate = new Date(startDate);
    endDate = new Date(endDate);

    const endPhraseString = endPhrase ?? "";

    const diffYears = Math.abs(differenceInYears(endDate, startDate));
    if (diffYears > 0) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NumOfYears, "", {
        num: `${diffYears}`,
      }).concat(endPhraseString);
    }

    const diffMonths = Math.abs(differenceInMonths(endDate, startDate));
    if (diffMonths > 0) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NumOfMonths, "", {
        num: `${diffMonths}`,
      }).concat(endPhraseString);
    }

    const diffWeeks = Math.abs(differenceInWeeks(endDate, startDate));
    if (diffWeeks > 0) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NumOfWeeks, "", {
        num: `${diffWeeks}`,
      }).concat(endPhraseString);
    }

    const diffDays = differenceInDays(endDate, startDate);
    if (diffDays > 0) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NumOfDaysBefore, "", {
        num: `${Math.abs(diffDays)}`,
      }).concat(endPhraseString);
    } else if (diffDays < 0) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NumOfDaysAfter, "", {
        num: `${Math.abs(diffDays)}`,
      }).concat(endPhraseString);
    }

    const diffHours = Math.abs(differenceInHours(endDate, startDate));
    if (diffHours > 0) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NumOfHours, "", {
        num: `${diffHours}`,
      }).concat(endPhraseString);
    }

    const diffMinutes = Math.abs(differenceInMinutes(endDate, startDate));
    if (diffMinutes > 0) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NumOfMinutes, "", {
        num: `${diffMinutes}`,
      }).concat(endPhraseString);
    }

    return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Now);
  },

  getUtcDate: (date: Date) => {
    return new Date(date.toISOString());
  },
  getDiffDays: (fromDate: Date, toDate: Date) => {
    return differenceInDays(new Date(toDate).setHours(0, 0, 0, 0), new Date(fromDate).setHours(0, 0, 0, 0));
  },
  getDiffMonths: (fromDate: Date, toDate: Date) => {
    return differenceInMonths(new Date(toDate), new Date(fromDate)) + 1;
  },
  addDays: (date: Date, days: number) => {
    return addDays(new Date(date), days);
  },
  addMonths: (date: Date, months: number) => {
    return addMonths(new Date(date), months);
  },
  addYears: (date: Date, years: number) => {
    return addYears(new Date(date), years);
  },
  getMinDate: (dates: (number | Date)[]) => {
    return min(dates);
  },
  getMaxDate: (dates: (number | Date)[]) => {
    return max(dates);
  },
  convertUTCDateToLocalDate: (date: Date) => {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  },
  convertDateToShortISOString: (date: Date | string) => {
    const dateObj = typeof date === "string" ? new Date(date) : date;

    if (!isValid(dateObj)) {
      throw new Error(
        `Invalid date provided to convertDateToShortISOString: ${date}. Date is of type ${typeof date} but should be of type 'Date'`
      );
    }

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  },

  getDatesBetween: (startDate: Date, endDate: Date) => {
    const dates = [];

    let currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= endDate) {
      dates.push(currentDate);
      currentDate = addDays(currentDate, 1);
    }
    return dates;
  },

  getHoursAndMinutes: (date: Date) => {
    const _date = new Date(date);
    const hours = _date.getHours();
    const minutes = _date.getMinutes();
    return `${hours < 10 ? `0` : ""}${hours}:${minutes < 10 ? `0` : ""}${minutes}`;
  },

  resetTime: (date?: Date): Date | undefined => {
    if (date) {
      const _date = new Date(date);
      _date.setHours(0, 0, 0, 0);
      return _date;
    }
    return date;
  },

  isBefore: isBefore,

  isEqual: (date: Date, dateToCompare: Date) => {
    return isEqual(new Date(date).setHours(0, 0, 0, 0), new Date(dateToCompare).setHours(0, 0, 0, 0));
  },

  getYearsBetween: (startDate: Date, endDate: Date): string[] => {
    const startYear = new Date(startDate).getFullYear();
    const endYear = new Date(endDate).getFullYear();

    const years = [];
    let currentYear = startYear;

    while (currentYear <= endYear) {
      years.push(currentYear.toString());
      currentYear++;
    }
    return years;
  },

  getMonthsBetween: (startDate: Date, endDate: Date): string[] => {
    const _startDate = new Date(startDate);
    const _endDate = new Date(endDate);

    const months = [];
    let currentDate = _startDate;

    while (currentDate <= _endDate) {
      months.push(LocalizationHelperInstance.getFormattedMessage(`Common.Month_${currentDate.getMonth() + 1}`));
      currentDate = addMonths(currentDate, 1);
    }
    return months;
  },

  isLatestDateWithinRange: (dateToCheck: Date, projectStart: Date, projectEnd: Date, memberStart: Date): boolean => {
    let validDate: Date;
    const memberStartDate: Date = DateHelper.resetTime(memberStart) as Date;
    if (dateToCheck === undefined) {
      validDate = DateHelper.resetTime(DateHelper.getDateMax()) as Date;
    } else {
      validDate = DateHelper.resetTime(dateToCheck) as Date;
    }

    const start = DateHelper.resetTime(new Date(projectStart)) as Date;
    const end = DateHelper.resetTime(new Date(projectEnd)) as Date;

    return validDate >= start && validDate <= end && validDate >= memberStartDate;
  },

  getDateMax: (): Date => {
    const maxDate = 8640000000000000;
    return new Date(maxDate);
  },

  withinIntervall: (dateToCheck: Date, start?: Date, end?: Date): boolean => {
    if (start === undefined) return false;

    const convertedStart = start == null ? new Date("0") : DateHelper.resetTime(new Date(start!));
    const convertedDateToCheck = dateToCheck == null ? new Date("0") : DateHelper.resetTime(new Date(dateToCheck!));

    DateHelper.resetTime(convertedStart);
    const convertedEnd = end == null ? new Date(DateHelper.getDateMax()) : DateHelper.resetTime(new Date(end!));
    DateHelper.resetTime(convertedEnd);

    return (
      (isEqual(convertedDateToCheck!, convertedStart!) || isAfter(convertedDateToCheck!, convertedStart!)) &&
      (isEqual(convertedDateToCheck!, convertedEnd!) || isBefore(convertedDateToCheck!, convertedEnd!))
    );
  },

  isValidDate: (value: any) => {
    return isValid(value);
  },

  getFirstDayOfWeek: (date: Date) => {
    const dayNumber = date.getDay();
    const daysToShift = (dayNumber === 0 ? DateConstants.TotalDaysOfWeek : dayNumber) - 1;
    return addDays(date, -daysToShift);
  },

  getLastDayOfWeek: (date: Date) => {
    const dayNumber = date.getDay();
    const daysToShift = dayNumber === 0 ? dayNumber : DateConstants.TotalDaysOfWeek - dayNumber;
    return addDays(date, daysToShift);
  },

  periodsAreOverlapping: (
    period1StartDate?: Date,
    period1EndDate?: Date,
    period2StartDate?: Date,
    period2EndDate?: Date
  ) => {
    const dateTimeMin = new Date(1, 1, 1);
    const dateTimeMax = new Date(9999, 12, 31);
    period1StartDate = period1StartDate === undefined ? dateTimeMin : new Date(period1StartDate);
    period1EndDate = period1EndDate === undefined ? dateTimeMax : new Date(period1EndDate);
    period2StartDate = period2StartDate === undefined ? dateTimeMin : new Date(period2StartDate);
    period2EndDate = period2EndDate === undefined ? dateTimeMax : new Date(period2EndDate);

    return (
      (period1StartDate.getTime() < period2EndDate.getTime() &&
        period1StartDate.getTime() > period2StartDate.getTime()) ||
      (period2StartDate.getTime() < period1EndDate.getTime() &&
        period2StartDate.getTime() > period1StartDate.getTime()) ||
      (period1EndDate.getTime() > period2StartDate.getTime() && period1EndDate.getTime() < period2EndDate.getTime()) ||
      (period2EndDate.getTime() > period1StartDate.getTime() && period2EndDate.getTime() < period1EndDate.getTime()) ||
      (period1StartDate.getTime() === period2StartDate.getTime() &&
        period1EndDate.getTime() === period2EndDate.getTime())
    );
  },

  splitYearMonthByCharacter: (yearMonth: number, character?: string) => {
    const yearMonthString = yearMonth.toString();
    return `${yearMonthString.substring(0, 4)}${character ?? "-"}${yearMonthString.substring(4, 6)}`;
  },

  splitDateToYearMonthByCharacter: (date: Date, character?: string) => {
    const yearMonthString = DateHelper.getYearMonth(date);
    return DateHelper.splitYearMonthByCharacter(yearMonthString, character);
  },

  isRangeEqual: (range: DateRange, rangeToCompare: DateRange) => {
    if (!range.start || !rangeToCompare.start || !range.end || !rangeToCompare.end) return false;
    return DateHelper.isEqual(range.start, rangeToCompare.start) && DateHelper.isEqual(range.end, rangeToCompare.end);
  },

  isLastDayofMonth: isLastDayOfMonth,

  fromAPIDate: (date?: Date): Date => new Date(date as any as string),

  isSplitFinancialYear: (date1: Date, date2: Date) => new Date(date1).getFullYear() !== new Date(date2).getFullYear(),

  formatDate: (date: Date) => {
    return LocalizationHelperInstance.getFormattedDate(date);
  },

  formatFinancialYear: (startDate: Date, endDate: Date, delimiter?: string) =>
    DateHelper.isSplitFinancialYear(startDate, endDate)
      ? `${DateHelper.formatDateRange(startDate, endDate, delimiter ?? "-")}`
      : new Date(startDate).getFullYear().toString(),

  formatAPIFinancialYear: (financialYearAsString: string): DateRange => {
    let financialYear: DateRange = {};

    if (financialYearAsString?.length > 0 && financialYearAsString?.includes(",")) {
      const dates = financialYearAsString.split(",");

      dates.forEach((date) => {
        return date.replace(" ", "");
      });

      financialYear = {
        start: new Date(dates[0]),
        end: new Date(dates[1]),
      };
    }

    return financialYear;
  },
};

export default DateHelper;
